/**
 * HeroSection Component
 *
 * Main hero section of the landing page with CTA buttons and app store badges.
 */

import { library } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faLayerGroup } from '@fortawesome/free-solid-svg-icons';
import { faApple, faGooglePlay } from '@fortawesome/free-brands-svg-icons';

// Register icons for this component
library.add(faCheckCircle, faLayerGroup, faApple, faGooglePlay);

import { FadeIn } from './FadeIn';
import { useVideoLoad } from '../hooks';

export function HeroSection() {
  const { failed: videoFailed, retryKey, handleLoadedData, handleError } = useVideoLoad();
  const assetsUrl = import.meta.env.VITE_PUBLIC_ASSETS_URL || '';

  return (
    <section className="container pt-16 pb-12 sm:pt-24 sm:pb-16 lg:pt-32 lg:pb-20">
      <FadeIn>
        <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight text-center md:text-left mb-8 lg:mb-12 w-full">
          Solução conectando <span className="text-secondary">Médicos</span> e <span className="text-primary">Pacientes</span>
        </h1>

        {/* Two Column Layout: Text (Left) + Preview (Right) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left: Text Content */}
          <div className="flex flex-col gap-6">
            <div className="space-y-4">
              <h2 className="text-xl sm:text-2xl font-semibold">O que é o Examfy?</h2>
              <p className="leading-normal text-muted-foreground sm:text-lg sm:leading-7">
                Solução completa que utiliza inteligência artificial para organizar exames, extrair
                dados de documentos e transcrever consultas, conectando pacientes e médicos e forma segura.
              </p>
              <p className="leading-normal text-muted-foreground sm:text-lg sm:leading-7">Gerencie exames,
                consultas e receitas em um histórico digital centralizado. Reduza redundância no histórico do
                paciente e elimine papéis perdidos.</p>
            </div>

            {/* Key Benefits */}
            <ul className="space-y-3 text-muted-foreground">
              <li className="flex items-start gap-3">
                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mt-1 shrink-0" />
                <span>Organizador online de exames e histórico digital</span>
              </li>
              <li className="flex items-start gap-3">
                <FontAwesomeIcon icon={faCheckCircle} className="text-blue-500 mt-1 shrink-0" />
                <span>Extração automática de exames e transcrição de consultas com IA</span>
              </li>
              <li className="flex items-start gap-3">
                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mt-1 shrink-0" />
                <span>Conexão segura entre pacientes e médicos</span>
              </li>
            </ul>

            {/* App Store Badges */}
            {/* TODO: Replace href="#" with actual app store URLs when available */}
            {/* <div className="flex gap-2 mt-2">
              <a
                href="#"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border bg-card hover:bg-accent transition-colors"
                aria-label="Baixe na App Store - em breve"
              >
                <FontAwesomeIcon icon={faApple} className="text-2xl" />
                <div className="text-left">
                  <div className="text-[10px] leading-tight">Baixe na</div>
                  <div className="text-sm font-semibold leading-tight">App Store</div>
                </div>
              </a>
              <a
                href="#"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border bg-card hover:bg-accent transition-colors"
                aria-label="Disponível no Google Play - em breve"
              >
                <FontAwesomeIcon icon={faGooglePlay} className="text-xl text-green-600" />
                <div className="text-left">
                  <div className="text-[10px] leading-tight">Disponível no</div>
                  <div className="text-sm font-semibold leading-tight">Google Play</div>
                </div>
              </a>
            </div> */}
          </div>

          {/* Right: Dashboard Preview */}
          <div className="relative w-full max-w-2xl mx-auto lg:max-w-none rounded-xl border bg-muted/50 p-2 shadow-2xl lg:rounded-2xl order-first lg:order-last">
            <div className="aspect-[16/9] rounded-lg bg-background border border-border flex items-center justify-center overflow-hidden relative">
              {/* Fallback content if video is missing or fails to load */}
              {videoFailed ? (
                <div className="absolute inset-0 bg-gradient-to-tr from-blue-50 to-green-50 dark:from-blue-950/20 dark:to-green-950/20 flex items-center justify-center">
                  <div className="text-center p-8">
                    <FontAwesomeIcon icon={faLayerGroup} className="text-6xl text-primary/20 mb-4" />
                    <p className="text-muted-foreground font-medium">Dashboard</p>
                  </div>
                </div>
              ) : (
                <video
                  key={retryKey}
                  className="absolute inset-0 w-full h-full object-cover rounded-lg"
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="auto"
                  onLoadedData={handleLoadedData}
                  onError={handleError}
                >
                  <source src={`${assetsUrl}/cdn/public/dashboard.mp4`} type="video/mp4" />
                </video>
              )}
            </div>
          </div>
        </div>
      </FadeIn>
    </section>
  );
}
